import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Popover from '@mui/material/Popover';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import Typography from '@mui/material/Typography'; // Import Typography
import { spermCryoStorageDetail } from '../cryoStorage.helpers';
import styles from './../oocyteCryo/cryoStorage.module.css';
import { getDateInDDMMYYYY, getLocalizedLabelByCode } from '../../../utils/commonUtils';
import { TypeOfSpermOptionsLocalized } from '../../../components/SemenProcessingComponent/SemenProcessingDetailsHelper';
import { actionOptionsLocalized } from '../../Embryology/Embryo_Culture/embryoCulture.helpers';


interface PopoverPanelProps {
  content: spermCryoStorageDetail; // Directly accepting the storage object 
}

const PopoverPanel: React.FC<PopoverPanelProps> = ({ content }) => {
  const { t } = useTranslation('material_information');
  const { t: tGeneral } = useTranslation('general');
  const language = localStorage.getItem('language');

  
  const [anchorEl, setAnchorEl] = useState<SVGSVGElement | null>(null);

  const handleClick = (event: React.MouseEvent<SVGSVGElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget); // Toggle Popover
  };

  const open = Boolean(anchorEl);

  // Mapping storage fields to labels and values
  const details = [
    { label: t('cycleNumber'), value: content.cycle_number ?? '-' },
    { label: t('patientName'), value: content.patient_name ?? '-' },
    { label: t('patientMRN'), value: content.patient_mrn ?? '-' },
    { label: t('sampleId'), value: content.sample_id ?? '-' },
    { label: t('typeOfSperm'), 
      value: `${tGeneral('Type')} - ${getLocalizedLabelByCode(TypeOfSpermOptionsLocalized, content.type_of_sperm || '', language)}`
    },
    { label: t('volumeMl'), value: content.volume ? `${content.volume} ml` : '-' },
    { label: t('semenVolumeMl'), value: content.semen_volume ?? '-' },
    { label: t('spermConcentrationMillionPerMl'), value: content.sperm_concentration_million_per_ml ?? '-' },
    { label: t('totalSpermCount'), value: content.total_sperm_count ?? '-' },
    { label: t('materialCount'), value: content.material_count ?? '-' },  
    { label: t('status'), 
      value: `${getLocalizedLabelByCode(actionOptionsLocalized, content.status || '', language)}`
    },
    { label: t('cryoDate'), value: getDateInDDMMYYYY(content.cryo_date) ?? '-' },
    { label: t('expiryDate'), value: content.expiry_date ?? '-' },
    { label: t('remark'), value: content.remark ?? '-' },
  ];

  return (
    <div style={{ display: 'inline-block',  }}>
      <InfoIcon
        onClick={handleClick}
        fontSize="small"
        style={{ cursor: 'pointer', color: '#EFAE78' }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        disableRestoreFocus
      >
        <div style={{ padding: '16px', width: '400px' }}>
          {/* Title inside the popover */}
          <Typography
            variant="h6"
            style={{
              fontWeight: 700,
              marginBottom: '8px',
              borderBottom: '1px solid #ddd',
              paddingBottom: '4px',
              fontFamily: 'Montserrat',
            }}
          >
            {t('materialInformation')}
          </Typography>
          {/* Render storage details */}
          {details.map((detail, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '6px',
              }}
            >
              <span className={styles.detailLabel}>
                {detail.label}
              </span>
              <span className={styles.detailValue}>
                {detail.value}
              </span>
            </div>
          ))}
        </div>
      </Popover>
    </div>
  );
};

export default PopoverPanel; 
